import { useState, useEffect, useCallback } from 'react';
import {
  SurveyResponseState,
  SurveyQuestionData,
  AnswerData,
  NavigationState,
  ValidationResult,
  SurveyData,
  QuestionType,
} from '../types';
import { useSessionManager } from './useSessionManager';
import {
  usePublicSurveyInstance,
  useCreateAnonymousSession,
  useSubmitAnswer,
  useCompleteSurveyResponse,
} from './useSurveyResponse';
import { usePostHog } from '../../../hooks/usePostHog';
import { SURVEY_RESPONSE_EVENTS } from '../../../utils/trackingEvents';

/**
 * Comprehensive hook for managing survey response flow
 * Handles state management, navigation, validation, and persistence
 */
export const useSurveyResponseManager = (instanceId: string, surveyId: string) => {
  const sessionManager = useSessionManager();
  const { captureEvent, identify, setGroup } = usePostHog();

  // Tracking state
  const [surveyStartTime, setSurveyStartTime] = useState<number | null>(null);
  const [questionStartTime, setQuestionStartTime] = useState<number | null>(null);
  const [lastMilestone, setLastMilestone] = useState<number>(0);

  // React Query hooks
  const {
    data: surveyInstance,
    isLoading: isLoadingSurvey,
    error: surveyError,
  } = usePublicSurveyInstance(instanceId, surveyId);
  const createSessionMutation = useCreateAnonymousSession();
  const submitAnswerMutation = useSubmitAnswer();
  const completeSurveyMutation = useCompleteSurveyResponse();

  // Local state
  const [surveyState, setSurveyState] = useState<SurveyResponseState>({
    currentQuestionIndex: 0,
    totalQuestions: 0,
    responses: {},
    sessionId: '',
    surveyInstanceId: instanceId,
    language: sessionManager.loadLanguagePreference(),
    isSubmitting: false,
    hasStarted: false,
    isCompleted: false,
  });

  const [navigationState, setNavigationState] = useState<NavigationState>({
    canGoBack: false,
    canGoNext: false,
    canComplete: false,
  });

  const [currentQuestion, setCurrentQuestion] = useState<SurveyQuestionData | null>(null);

  // Update navigation state based on current state and questions
  const updateNavigationState = useCallback(
    (state: SurveyResponseState, questions: SurveyQuestionData[]) => {
      const canGoBack = state.currentQuestionIndex > 0;
      const currentQ = questions[state.currentQuestionIndex];
      const hasCurrentAnswer = currentQ ? state.responses[currentQ.id] !== undefined : false;
      const canGoNext =
        state.currentQuestionIndex < questions.length - 1 &&
        (!currentQ?.isRequired || hasCurrentAnswer);
      const canComplete =
        state.currentQuestionIndex === questions.length - 1 &&
        (!currentQ?.isRequired || hasCurrentAnswer);

      setNavigationState({
        canGoBack,
        canGoNext,
        canComplete,
        currentQuestion: currentQ,
        nextQuestion: questions[state.currentQuestionIndex + 1],
        previousQuestion: questions[state.currentQuestionIndex - 1],
      });
    },
    []
  );

  // Initialize survey state from localStorage and survey data
  useEffect(() => {
    if (surveyInstance) {
      const questions = surveyInstance.instanceData.questions
        .sort((a, b) => a.order - b.order)
        .map((q) => ({ ...q, type: q.type as QuestionType }));
      const savedState = sessionManager.loadResponseState(instanceId);
      const savedQuestionIndex = sessionManager.loadCurrentQuestion(instanceId);

      const initialState: SurveyResponseState = {
        currentQuestionIndex: savedState?.currentQuestionIndex ?? savedQuestionIndex,
        totalQuestions: questions.length,
        responses: savedState?.responses ?? {},
        sessionId: savedState?.sessionId ?? sessionManager.getOrCreateSessionId(instanceId),
        surveyInstanceId: instanceId,
        language: savedState?.language ?? sessionManager.loadLanguagePreference(),
        isSubmitting: false,
        hasStarted: savedState?.hasStarted ?? sessionManager.hasStartedSurvey(instanceId),
        isCompleted: savedState?.isCompleted ?? sessionManager.hasCompletedSurvey(instanceId),
      };

      // Track session resume if returning to ongoing survey
      if (initialState.hasStarted && !initialState.isCompleted && savedState) {
        captureEvent(SURVEY_RESPONSE_EVENTS.SESSION_RESUMED, {
          instance_id: instanceId,
          session_id: initialState.sessionId,
          survey_id: surveyInstance.instanceData.survey.id,
          survey_title: surveyInstance.instanceData.survey.title,
          language: initialState.language,
          current_question_index: initialState.currentQuestionIndex,
          total_questions: initialState.totalQuestions,
          questions_answered: Object.keys(initialState.responses).length,
          completion_percentage:
            (Object.keys(initialState.responses).length / initialState.totalQuestions) * 100,
        });
      }

      setSurveyState(initialState);
      setCurrentQuestion(questions[initialState.currentQuestionIndex] || null);

      // Identify survey respondent in PostHog using sessionId
      if (initialState.sessionId) {
        identify(initialState.sessionId, {
          survey_instance_id: instanceId,
          survey_id: surveyInstance.instanceData.survey.id,
          survey_title: surveyInstance.instanceData.survey.title,
        });

        // Set survey instance group for anonymous respondents
        setGroup('survey_instance', instanceId, {
          id: instanceId,
          survey_id: surveyInstance.instanceData.survey.id,
          survey_title: surveyInstance.instanceData.survey.title,
          status: 'active',
        });
      }
      updateNavigationState(initialState, questions);
      setQuestionStartTime(Date.now());
    }
  }, [
    surveyInstance,
    instanceId,
    captureEvent,
    identify,
    setGroup,
    sessionManager,
    updateNavigationState,
  ]);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (surveyState.sessionId) {
      sessionManager.saveResponseState(instanceId, surveyState);
      sessionManager.saveCurrentQuestion(instanceId, surveyState.currentQuestionIndex);
      sessionManager.saveLanguagePreference(surveyState.language);
    }
  }, [surveyState, instanceId, sessionManager]);

  // Start survey
  const startSurvey = useCallback(async () => {
    if (!surveyInstance) return;

    const sessionContext = sessionManager.createSessionContext(instanceId, surveyState.language);
    const startTime = Date.now();

    try {
      // Create anonymous session
      await createSessionMutation.mutateAsync({
        instanceId,
        surveyId,
        sessionData: {
          surveyInstanceId: instanceId,
          sessionId: sessionContext.sessionId,
          surveyId: surveyId,
          userAgent: sessionContext.userAgent,
          language: sessionContext.language,
        },
      });

      // Track session creation and survey start
      captureEvent(SURVEY_RESPONSE_EVENTS.SESSION_CREATED, {
        instance_id: instanceId,
        session_id: sessionContext.sessionId,
        survey_id: surveyInstance.instanceData.survey.id,
        survey_title: surveyInstance.instanceData.survey.title,
        language: sessionContext.language,
        user_agent: sessionContext.userAgent,
        total_questions: surveyInstance.instanceData.questions.length,
      });

      captureEvent(SURVEY_RESPONSE_EVENTS.SURVEY_STARTED, {
        instance_id: instanceId,
        session_id: sessionContext.sessionId,
        survey_id: surveyInstance.instanceData.survey.id,
        survey_title: surveyInstance.instanceData.survey.title,
        language: sessionContext.language,
        total_questions: surveyInstance.instanceData.questions.length,
      });

      // Mark as started
      sessionManager.markSurveyStarted(instanceId);

      setSurveyState((prev) => ({
        ...prev,
        hasStarted: true,
        sessionId: sessionContext.sessionId,
      }));

      setSurveyStartTime(startTime);
      setQuestionStartTime(startTime);
    } catch (error) {
      console.error('Failed to start survey:', error);

      captureEvent(SURVEY_RESPONSE_EVENTS.ERROR_ENCOUNTERED, {
        instance_id: instanceId,
        error_type: 'session_creation_failed',
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });

      throw error;
    }
  }, [
    surveyInstance,
    instanceId,
    surveyId,
    surveyState.language,
    sessionManager,
    createSessionMutation,
    captureEvent,
  ]);

  // Navigate to next question
  const goToNextQuestion = useCallback(() => {
    if (!surveyInstance) return;

    const questions = surveyInstance.instanceData.questions
      .sort((a, b) => a.order - b.order)
      .map((q) => ({ ...q, type: q.type as QuestionType }));
    const newIndex = Math.min(surveyState.currentQuestionIndex + 1, questions.length - 1);

    // Track progress milestones
    const progressPercentage = ((newIndex + 1) / questions.length) * 100;
    const milestoneThresholds = [25, 50, 75];

    for (const threshold of milestoneThresholds) {
      if (progressPercentage >= threshold && lastMilestone < threshold) {
        captureEvent(SURVEY_RESPONSE_EVENTS.PROGRESS_MILESTONE, {
          instance_id: instanceId,
          session_id: surveyState.sessionId,
          milestone_percentage: threshold,
          current_question_index: newIndex,
          total_questions: questions.length,
          questions_answered: Object.keys(surveyState.responses).length,
          time_to_milestone_seconds: surveyStartTime ? (Date.now() - surveyStartTime) / 1000 : null,
        });
        setLastMilestone(threshold);
        break;
      }
    }

    setSurveyState((prev) => ({
      ...prev,
      currentQuestionIndex: newIndex,
    }));

    setCurrentQuestion(questions[newIndex] || null);
    updateNavigationState({ ...surveyState, currentQuestionIndex: newIndex }, questions);
    setQuestionStartTime(Date.now());
  }, [
    surveyInstance,
    surveyState,
    updateNavigationState,
    instanceId,
    lastMilestone,
    surveyStartTime,
    captureEvent,
  ]);

  // Navigate to previous question
  const goToPreviousQuestion = useCallback(() => {
    if (!surveyInstance) return;

    const questions = surveyInstance.instanceData.questions
      .sort((a, b) => a.order - b.order)
      .map((q) => ({ ...q, type: q.type as QuestionType }));
    const newIndex = Math.max(surveyState.currentQuestionIndex - 1, 0);

    setSurveyState((prev) => ({
      ...prev,
      currentQuestionIndex: newIndex,
    }));

    setCurrentQuestion(questions[newIndex] || null);
    updateNavigationState({ ...surveyState, currentQuestionIndex: newIndex }, questions);
    setQuestionStartTime(Date.now());
  }, [surveyInstance, surveyState, updateNavigationState]);

  // Update answer locally without submitting to API
  const updateAnswerLocally = useCallback(
    (
      questionId: string,
      value: string | string[] | number | boolean | undefined,
      comment?: string
    ) => {
      const answerData: AnswerData = {
        questionId,
        value,
        comment,
        answeredAt: new Date().toISOString(),
      };

      // Update local state only
      setSurveyState((prev) => {
        const newState = {
          ...prev,
          responses: {
            ...prev.responses,
            [questionId]: answerData,
          },
        };

        // Update navigation state
        if (surveyInstance) {
          const questions = surveyInstance.instanceData.questions
            .sort((a, b) => a.order - b.order)
            .map((q) => ({ ...q, type: q.type as QuestionType }));
          updateNavigationState(newState, questions);
        }

        return newState;
      });
    },
    [surveyInstance, updateNavigationState]
  );

  // Submit answer for current question
  const submitAnswer = useCallback(
    async (
      questionId: string,
      value: string | string[] | number | boolean | undefined,
      comment?: string
    ) => {
      if (!surveyState.sessionId || !currentQuestion) return;

      const responseTime = questionStartTime ? (Date.now() - questionStartTime) / 1000 : null;
      const answerData: AnswerData = {
        questionId,
        value,
        comment,
        answeredAt: new Date().toISOString(),
      };

      try {
        // Submit to API
        await submitAnswerMutation.mutateAsync({
          instanceId,
          sessionId: surveyState.sessionId,
          surveyId,
          answerData: {
            questionId,
            value,
            comment,
          },
        });

        // Track answer submission
        captureEvent(SURVEY_RESPONSE_EVENTS.ANSWER_SUBMITTED, {
          instance_id: instanceId,
          session_id: surveyState.sessionId,
          question_id: questionId,
          question_type: currentQuestion.type,
          question_order: currentQuestion.order,
          question_required: currentQuestion.isRequired,
          has_comment: !!comment,
          answer_type: 'final',
          response_time_seconds: responseTime,
          current_question_index: surveyState.currentQuestionIndex,
          total_questions: surveyState.totalQuestions,
          questions_answered: Object.keys(surveyState.responses).length + 1,
        });

        // Track comment addition separately if provided
        if (comment) {
          captureEvent(SURVEY_RESPONSE_EVENTS.COMMENT_ADDED, {
            instance_id: instanceId,
            session_id: surveyState.sessionId,
            question_id: questionId,
            comment_length: comment.length,
          });
        }

        // Update local state
        setSurveyState((prev) => {
          const newState = {
            ...prev,
            responses: {
              ...prev.responses,
              [questionId]: answerData,
            },
          };

          // Update navigation state
          if (surveyInstance) {
            const questions = surveyInstance.instanceData.questions
              .sort((a, b) => a.order - b.order)
              .map((q) => ({ ...q, type: q.type as QuestionType }));
            updateNavigationState(newState, questions);
          }

          return newState;
        });
      } catch (error) {
        console.error('Failed to submit answer:', error);

        // Check if survey was closed mid-submission
        if (
          error &&
          typeof error === 'object' &&
          'code' in error &&
          error.code === 'SURVEY_INSTANCE_CLOSED'
        ) {
          captureEvent(SURVEY_RESPONSE_EVENTS.ERROR_ENCOUNTERED, {
            instance_id: instanceId,
            error_type: 'survey_closed_mid_submission',
            error_message: 'This survey is no longer accepting responses',
            question_id: questionId,
          });

          const closedError = new Error('This survey is no longer accepting responses');
          (closedError as any).code = 'SURVEY_INSTANCE_CLOSED';
          throw closedError;
        }

        captureEvent(SURVEY_RESPONSE_EVENTS.ERROR_ENCOUNTERED, {
          instance_id: instanceId,
          error_type: 'answer_submission_failed',
          error_message: error instanceof Error ? error.message : 'Unknown error',
          question_id: questionId,
        });

        throw error;
      }
    },
    [
      surveyState.sessionId,
      instanceId,
      surveyId,
      submitAnswerMutation,
      surveyInstance,
      updateNavigationState,
      currentQuestion,
      questionStartTime,
      surveyState.currentQuestionIndex,
      surveyState.totalQuestions,
      surveyState.responses,
      captureEvent,
    ]
  );

  // Complete survey
  const completeSurvey = useCallback(async () => {
    if (!surveyState.sessionId || !surveyInstance) return;

    setSurveyState((prev) => ({ ...prev, isSubmitting: true }));

    try {
      const result = await completeSurveyMutation.mutateAsync({
        instanceId,
        sessionId: surveyState.sessionId,
        surveyId,
      });

      const completionTime = Date.now();
      const totalDuration = surveyStartTime ? (completionTime - surveyStartTime) / 1000 : null;

      // Track survey completion
      captureEvent(SURVEY_RESPONSE_EVENTS.SURVEY_COMPLETED, {
        instance_id: instanceId,
        session_id: surveyState.sessionId,
        survey_id: surveyInstance.instanceData.survey.id,
        survey_title: surveyInstance.instanceData.survey.title,
        language: surveyState.language,
        total_questions: surveyState.totalQuestions,
        questions_answered: Object.keys(surveyState.responses).length,
        completion_percentage:
          (Object.keys(surveyState.responses).length / surveyState.totalQuestions) * 100,
        session_duration_seconds: totalDuration,
        completed_at: new Date().toISOString(),
      });

      // Mark as completed
      sessionManager.markSurveyCompleted(instanceId);

      setSurveyState((prev) => ({
        ...prev,
        isCompleted: true,
        isSubmitting: false,
      }));

      return result;
    } catch (error) {
      setSurveyState((prev) => ({ ...prev, isSubmitting: false }));
      console.error('Failed to complete survey:', error);

      // Check if survey was closed mid-completion
      if (
        error &&
        typeof error === 'object' &&
        'code' in error &&
        error.code === 'SURVEY_INSTANCE_CLOSED'
      ) {
        captureEvent(SURVEY_RESPONSE_EVENTS.ERROR_ENCOUNTERED, {
          instance_id: instanceId,
          error_type: 'survey_closed_mid_completion',
          error_message: 'This survey is no longer accepting responses',
        });

        const closedError = new Error('This survey is no longer accepting responses');
        (closedError as any).code = 'SURVEY_INSTANCE_CLOSED';
        throw closedError;
      }

      captureEvent(SURVEY_RESPONSE_EVENTS.ERROR_ENCOUNTERED, {
        instance_id: instanceId,
        error_type: 'survey_completion_failed',
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });

      throw error;
    }
  }, [
    surveyState.sessionId,
    instanceId,
    surveyId,
    completeSurveyMutation,
    sessionManager,
    surveyInstance,
    surveyState.language,
    surveyState.totalQuestions,
    surveyState.responses,
    surveyStartTime,
    captureEvent,
  ]);

  // Change language
  const changeLanguage = useCallback(
    (language: 'en' | 'no') => {
      const previousLanguage = surveyState.language;

      if (previousLanguage !== language) {
        captureEvent(SURVEY_RESPONSE_EVENTS.LANGUAGE_SWITCHED, {
          instance_id: instanceId,
          session_id: surveyState.sessionId,
          from_language: previousLanguage,
          to_language: language,
          current_question_index: surveyState.currentQuestionIndex,
          total_questions: surveyState.totalQuestions,
        });
      }

      setSurveyState((prev) => ({
        ...prev,
        language,
      }));
      sessionManager.saveLanguagePreference(language);
    },
    [
      sessionManager,
      surveyState.language,
      instanceId,
      surveyState.sessionId,
      surveyState.currentQuestionIndex,
      surveyState.totalQuestions,
      captureEvent,
    ]
  );

  // Validate current question answer
  const validateCurrentAnswer = useCallback((): ValidationResult => {
    if (!currentQuestion) {
      return { isValid: true, errors: [], warnings: [] };
    }

    const answer = surveyState.responses[currentQuestion.id];
    const errors: ValidationResult['errors'] = [];
    const warnings: ValidationResult['warnings'] = [];

    // Required field validation
    if (currentQuestion.isRequired && (answer?.value === undefined || answer?.value === '')) {
      errors.push({
        questionId: currentQuestion.id,
        errorType: 'REQUIRED' as const,
        message:
          surveyState.language === 'no' ? 'Dette feltet er påkrevd' : 'This field is required',
      });
    }

    // Type-specific validation
    if (answer?.value !== undefined) {
      const validationRules = currentQuestion.validationRules;

      if (validationRules) {
        // Text length validation
        if (typeof answer.value === 'string') {
          if (validationRules.minLength && answer.value.length < validationRules.minLength) {
            errors.push({
              questionId: currentQuestion.id,
              errorType: 'INVALID_FORMAT' as const,
              message:
                surveyState.language === 'no'
                  ? `Teksten må være minst ${validationRules.minLength} tegn`
                  : `Text must be at least ${validationRules.minLength} characters`,
            });
          }

          if (validationRules.maxLength && answer.value.length > validationRules.maxLength) {
            errors.push({
              questionId: currentQuestion.id,
              errorType: 'INVALID_FORMAT' as const,
              message:
                surveyState.language === 'no'
                  ? `Teksten kan ikke være mer enn ${validationRules.maxLength} tegn`
                  : `Text cannot be more than ${validationRules.maxLength} characters`,
            });
          }
        }

        // Number range validation
        if (typeof answer.value === 'number') {
          if (validationRules.minValue !== undefined && answer.value < validationRules.minValue) {
            errors.push({
              questionId: currentQuestion.id,
              errorType: 'OUT_OF_RANGE' as const,
              message:
                surveyState.language === 'no'
                  ? `Verdien må være minst ${validationRules.minValue}`
                  : `Value must be at least ${validationRules.minValue}`,
            });
          }

          if (validationRules.maxValue !== undefined && answer.value > validationRules.maxValue) {
            errors.push({
              questionId: currentQuestion.id,
              errorType: 'OUT_OF_RANGE' as const,
              message:
                surveyState.language === 'no'
                  ? `Verdien kan ikke være mer enn ${validationRules.maxValue}`
                  : `Value cannot be more than ${validationRules.maxValue}`,
            });
          }
        }
      }
    }

    const result = {
      isValid: errors.length === 0,
      errors,
      warnings,
    };

    // Track validation errors
    if (errors.length > 0) {
      captureEvent(SURVEY_RESPONSE_EVENTS.VALIDATION_ERROR, {
        instance_id: instanceId,
        session_id: surveyState.sessionId,
        question_id: currentQuestion.id,
        question_type: currentQuestion.type,
        error_types: errors.map((error) => error.errorType),
        error_count: errors.length,
        is_required: currentQuestion.isRequired,
      });
    }

    return result;
  }, [
    currentQuestion,
    surveyState.responses,
    surveyState.language,
    instanceId,
    surveyState.sessionId,
    captureEvent,
  ]);

  // Get normalized survey data
  const getSurveyData = useCallback((): SurveyData | null => {
    if (!surveyInstance) return null;

    const { survey, questions } = surveyInstance.instanceData;
    const sortedQuestions = questions
      .sort((a, b) => a.order - b.order)
      .map((q) => ({ ...q, type: q.type as QuestionType }));

    return {
      id: survey.id,
      title: survey.title,
      description: survey.description,
      questions: sortedQuestions,
      totalQuestions: sortedQuestions.length,
    };
  }, [surveyInstance]);

  return {
    // State
    surveyState,
    navigationState,
    currentQuestion,

    // Data
    surveyData: getSurveyData(),
    isLoading: isLoadingSurvey,
    error: surveyError,

    // Actions
    startSurvey,
    goToNextQuestion,
    goToPreviousQuestion,
    submitAnswer,
    updateAnswerLocally,
    completeSurvey,
    changeLanguage,
    validateCurrentAnswer,

    // Utilities
    canSubmitResponse: sessionManager.canSubmitResponse(instanceId),
    hasStartedSurvey: sessionManager.hasStartedSurvey(instanceId),
    hasCompletedSurvey: sessionManager.hasCompletedSurvey(instanceId),
  };
};
